import mapValues from 'lodash-es/mapValues'
import omit from 'lodash-es/omit'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  ENTITY_LISTING_VIEW_TYPE,
} from '../../../constants/common'
import { DESC_ORDER_SYMBOL } from '../../../constants/sort'
import {
  getListViewTypeWithoutPreview,
  getListViewTypeWithPreview,
  isListViewTypeBoard,
  isListViewTypeWithPreview,
} from '../../../utils/listViewType'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../../utils/massActions'
import { TextListItem } from '../../ListItem'
import {
  APPLICANTS_FILTER_PARAM,
  getIsMockApplicantId,
} from '../../RentalPortalApplicant'
import { getListingUnitLabel } from '../../RentalPortalListing'
import { APPLICANT_PROFILE_ARCHIVE_BLOCKED_STAGES } from '../RentalPortalApplicantProfile'
import { getHasMatchScore } from '../RentalPortalMatch'

import {
  APPLICANT_ACTION_ARCHIVE,
  APPLICANT_ACTION_ARCHIVE_MASS,
  APPLICANT_ACTION_OPEN,
  APPLICANT_COLUMN_CONFIG,
  APPLICANT_COLUMN_NAME,
  APPLICANT_COMPARE_VIEW_TYPE,
  APPLICANT_HIDDEN_COLUMNS,
  APPLICANT_PREVIEW_REGIME_AVAILABLE,
  APPLICANT_ROW_ACTIONS,
  APPLICANT_SORTABLE_COLUMNS,
  APPLICANT_VIEW_REGIME,
  APPLICANT_VIEW_REGIME_LABEL_KEY,
  APPLICANTS_AVAILABLE_FILTERS,
  APPLICANTS_FILTER_OPTION_LIST_ITEM,
  APPLICANTS_PAYLOAD_FILTER_PARAM_GATE,
} from './RentalPortalApplicants.constants'

import BoardViewIcon from '../../../icons/board-view.svg'
import CompareViewIcon from '../../../icons/compare-view.svg'
import PreviewIcon from '../../../icons/preview.svg'
import RentalPortalIcon from '../../../icons/rental-portal.svg'
import TableViewIcon from '../../../icons/table-view.svg'

function toTranslatedApplicantsFilterOption(
  { label_key: labelKey, ...option },
  t
) {
  return labelKey ? { ...option, label: t(labelKey) } : option
}

function getApplicantArchiveRowAction(applicant) {
  const isArchiveBlocked = APPLICANT_PROFILE_ARCHIVE_BLOCKED_STAGES.includes(
    applicant.stage
  )

  return {
    disabled: isArchiveBlocked,
    label: 'RentalPortal.ApplicantArchiveActionOptionKey',
    permission: applicant.permissions?.can_archive,
    tooltipContent: isArchiveBlocked
      ? 'RentalPortal.ApplicantProfileArchiveBlockedTooltipKey'
      : null,
    value: APPLICANT_ACTION_ARCHIVE,
  }
}

function getIsApplicantsFitColumn(name) {
  return name === APPLICANT_COLUMN_NAME.Fit
}

function getApplicantsListingListItemProps() {
  return { ListIcon: RentalPortalIcon }
}

function getIsApplicantsListingFilter(filterName) {
  return filterName === APPLICANTS_FILTER_PARAM.listing
}

function getApplicantsFilterOptionItemProps(filterName) {
  if (getIsApplicantsListingFilter(filterName)) {
    return {
      ListItem: TextListItem,
      getListItemProps: getApplicantsListingListItemProps,
    }
  }

  const FilterListItem = APPLICANTS_FILTER_OPTION_LIST_ITEM[filterName]

  return FilterListItem ? { ListItem: FilterListItem } : null
}

export function getTranslatedApplicantsAvailableFilters({
  availableFilters,
  t,
}) {
  return availableFilters.map(filter =>
    filter.options
      ? {
          ...filter,
          options: filter.options.map(option => ({
            ...toTranslatedApplicantsFilterOption(option, t),
            ...getApplicantsFilterOptionItemProps(filter.name),
          })),
        }
      : filter
  )
}

export function getApplicantsColumns() {
  return mapValues(APPLICANT_COLUMN_CONFIG, (config, name) => ({
    ...config,
    name,
    ordering_field: APPLICANT_SORTABLE_COLUMNS.includes(name) ? name : null,
    visible: !APPLICANT_HIDDEN_COLUMNS.includes(name),
  }))
}

export function getHasApplicantsFitOrdering(ordering) {
  return (ordering ?? EMPTY_LIST).some(entry =>
    getIsApplicantsFitColumn(entry.replace(DESC_ORDER_SYMBOL, EMPTY_STRING))
  )
}

export function getHasApplicantsMatchScore(items) {
  return items.length === 0 || items.some(item => getHasMatchScore(item.match))
}

export function getHasApplicantsPayloadField({ field, items }) {
  return items.length === 0 || items.some(item => Object.hasOwn(item, field))
}

export function getApplicantsPayloadColumns({
  columns,
  hasAttention,
  hasFitScore,
  hasMatch,
}) {
  const omittedColumns = []

  if (!hasAttention) {
    omittedColumns.push(APPLICANT_COLUMN_NAME.WaitingOn)
  }

  if (!hasMatch) {
    omittedColumns.push(APPLICANT_COLUMN_NAME.Chips, APPLICANT_COLUMN_NAME.Fit)
  }

  return mapValues(omit(columns, omittedColumns), (column, name) =>
    getIsApplicantsFitColumn(name) && !hasFitScore
      ? { ...column, ordering_field: null }
      : column
  )
}

export function getApplicantsPayloadAvailableFilters({
  availableFilters,
  hasAttention,
  hasMatch,
}) {
  const gates = { hasAttention, hasMatch }

  return availableFilters.filter(filter => {
    const gate = APPLICANTS_PAYLOAD_FILTER_PARAM_GATE[filter.query_param]

    return !gate || gates[gate]
  })
}

export function getApplicantViewRegimeOptions({
  hasStage,
  isCompareAvailable,
  isSplitAvailable,
  listViewType,
}) {
  const isCompareBody = getIsApplicantCompareViewType(listViewType)

  const isBoardBody = isListViewTypeBoard(listViewType)

  const hasPreview = isListViewTypeWithPreview(listViewType)

  return [
    {
      Icon: TableViewIcon,
      isActive: !isBoardBody && !isCompareBody,
      label: APPLICANT_VIEW_REGIME_LABEL_KEY[APPLICANT_VIEW_REGIME.table],
      name: APPLICANT_VIEW_REGIME.table,
      value: hasPreview
        ? ENTITY_LISTING_VIEW_TYPE.tableWithPreview
        : ENTITY_LISTING_VIEW_TYPE.table,
    },
    ...(APPLICANT_PREVIEW_REGIME_AVAILABLE && isSplitAvailable
      ? [
          {
            Icon: PreviewIcon,
            isActive: hasPreview,
            label: APPLICANT_VIEW_REGIME_LABEL_KEY[APPLICANT_VIEW_REGIME.split],
            menuLabel: hasPreview
              ? APPLICANT_VIEW_REGIME_LABEL_KEY[
                  isBoardBody
                    ? APPLICANT_VIEW_REGIME.board
                    : APPLICANT_VIEW_REGIME.table
                ]
              : APPLICANT_VIEW_REGIME_LABEL_KEY[APPLICANT_VIEW_REGIME.split],
            name: APPLICANT_VIEW_REGIME.split,
            value: hasPreview
              ? getListViewTypeWithoutPreview(listViewType)
              : getListViewTypeWithPreview(listViewType),
          },
        ]
      : EMPTY_LIST),
    ...(hasStage
      ? [
          {
            Icon: BoardViewIcon,
            isActive: isBoardBody,
            label: APPLICANT_VIEW_REGIME_LABEL_KEY[APPLICANT_VIEW_REGIME.board],
            name: APPLICANT_VIEW_REGIME.board,
            value: hasPreview
              ? ENTITY_LISTING_VIEW_TYPE.boardWithPreview
              : ENTITY_LISTING_VIEW_TYPE.board,
          },
        ]
      : EMPTY_LIST),
    ...(isCompareAvailable
      ? [
          {
            Icon: CompareViewIcon,
            isActive: isCompareBody,
            label:
              APPLICANT_VIEW_REGIME_LABEL_KEY[APPLICANT_VIEW_REGIME.compare],
            name: APPLICANT_VIEW_REGIME.compare,
            value: APPLICANT_COMPARE_VIEW_TYPE,
          },
        ]
      : EMPTY_LIST),
  ]
}

export function getApplicantWithHighlightBudget(item) {
  return item.attention?.needs_you ? { ...item, unseen: false } : item
}

export function getApplicantItemActions(item) {
  return item?.permissions?.can_view
    ? parseActionsOnValidDivider(
        filterPermittedActionsWithDividers([
          ...APPLICANT_ROW_ACTIONS,
          null,
          getApplicantArchiveRowAction(item),
        ])
      )
    : EMPTY_LIST
}

export function getApplicantsFilteredEmptyTextKey(filterValues) {
  return filterValues[APPLICANTS_FILTER_PARAM.needsMyAction]
    ? 'RentalPortal.ListingNeedsMyActionNoResultsKey'
    : 'RentalPortal.ApplicantsNoFilterResultsKey'
}

export function getApplicantsRefreshErrorText({
  availableFilters,
  filterValues,
  t,
}) {
  const activeFilterTitles = availableFilters
    .filter(filter => filterValues[filter.query_param])
    .map(filter => t(filter.title))

  return activeFilterTitles.length > 0
    ? t('RentalPortal.ApplicantsRefreshErrorFiltersKey', {
        filters: activeFilterTitles.join(', '),
      })
    : t('RentalPortal.ApplicantsRefreshErrorKey')
}

// TODO: archive backend applicants once the archive section and mass actions read the backend
export function getIsApplicantArchivable(applicant) {
  return (
    getIsMockApplicantId(applicant?.id) &&
    applicant?.permissions?.can_archive &&
    !APPLICANT_PROFILE_ARCHIVE_BLOCKED_STAGES.includes(applicant?.stage)
  )
}

export function getApplicantsMassActions({ eligibleCount, selectedSize, t }) {
  const hasUnusableSelection = selectedSize > 0 && eligibleCount === 0

  const items = [
    {
      disabled: hasUnusableSelection,
      label: t('RentalPortal.ApplicantArchiveActionOptionKey'),
      permission: true,
      tooltipContent: hasUnusableSelection
        ? t('RentalPortal.ApplicantArchiveMassNotAllowedTooltipKey')
        : null,
      value: APPLICANT_ACTION_ARCHIVE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getIsApplicantCompareViewType(listViewType) {
  return listViewType === APPLICANT_COMPARE_VIEW_TYPE
}

export function getApplicantsAvailableFilters({ listingOptions, t }) {
  return APPLICANTS_AVAILABLE_FILTERS.map(filter =>
    getIsApplicantsListingFilter(filter.name)
      ? {
          ...filter,
          options: listingOptions.map(option => ({
            label: getListingUnitLabel({ listing: option.label, t }),
            value: option.value,
          })),
        }
      : filter
  )
}

export function getIsApplicantActionOpen(action) {
  return action === APPLICANT_ACTION_OPEN
}

export function getIsApplicantActionArchive(action) {
  return action === APPLICANT_ACTION_ARCHIVE
}
